import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import { useHomepageSectionMutation } from '@/hooks/useHomepageSectionMutation';
import QuickEditInline from '@/components/quickedit/QuickEditInline';
import QuickEditImage from '@/components/quickedit/QuickEditImage';
import ImageWithFallback from '@/components/ui/ImageWithFallback';
import SectionPhotoCredit from '@/components/homepage/SectionPhotoCredit';

interface StorySectionProps {
  section: any;
}

export default function StorySection({ section }: StorySectionProps) {
  const content = section.content ?? {};
  const imagePosition = content.image_position || 'left';
  const body = content.body || '';
  const ctaText = content.cta_text;
  const ctaLink = content.cta_link;
  const headingId = `story-heading-${section.id}`;
  const imageAlt = content.image_alt || section.title || 'Our story';

  const { isActive } = useQuickEdit();
  const mutation = useHomepageSectionMutation(section.id);

  return (
    <section aria-labelledby={headingId} className="bg-[--bg-primary]">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className={cn(
          'grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16 items-center',
          imagePosition === 'right' && 'lg:[direction:rtl]'
        )}>
          {/* Image */}
          <div className={cn('lg:[direction:ltr]')}>
            <div className="overflow-hidden rounded-xl aspect-[4/3]">
              <QuickEditImage
                src={content.image_url}
                onSave={(url) => mutation.mutateAsync({ content: { ...section.content, image_url: url } })}
                label="Story image"
                targetWidth={1200}
                targetHeight={900}
                specKey="story.image_url"
              >
                <ImageWithFallback
                  src={content.image_url}
                  alt={imageAlt}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <SectionPhotoCredit content={content} />
              </QuickEditImage>
            </div>
          </div>

          {/* Content */}
          <div className="lg:[direction:ltr]">
            <h2
              id={headingId}
              className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
            >
              {isActive ? (
                <QuickEditInline
                  value={section.title}
                  onSave={(val) => mutation.mutateAsync({ title: val, content: section.content })}
                  label="Story title"
                  as="span"
                  className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
                />
              ) : section.title}
            </h2>
            {section.subtitle && (
              <p className="mt-2 font-display italic text-lg text-sage-600">
                {isActive ? (
                  <QuickEditInline
                    value={section.subtitle}
                    onSave={(val) => mutation.mutateAsync({ subtitle: val, content: section.content })}
                    label="Story subtitle"
                    as="span"
                    className="font-display italic text-lg text-sage-600"
                  />
                ) : section.subtitle}
              </p>
            )}
            {body && (
              <div className="mt-4 prose-boutique text-sage-700 leading-relaxed whitespace-pre-line">
                {isActive ? (
                  <QuickEditInline
                    value={body}
                    onSave={(val) => mutation.mutateAsync({ content: { ...section.content, body: val } })}
                    label="Story body text"
                    as="span"
                    className="text-sage-700 leading-relaxed"
                    multiline
                  />
                ) : body}
              </div>
            )}
            {ctaText && ctaLink && (
              <div className="mt-6">
                <Link
                  to={ctaLink}
                  className="inline-flex items-center gap-2 text-sm font-medium text-sage-700 hover:text-sage-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded"
                >
                  {isActive ? (
                    <QuickEditInline
                      value={ctaText}
                      onSave={(val) => mutation.mutateAsync({ content: { ...section.content, cta_text: val } })}
                      label="Story CTA text"
                      as="span"
                      className="text-sm font-medium text-sage-700"
                    />
                  ) : ctaText}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
